import { useEffect, useMemo, useState } from 'react';

import { deepNullToUndefined } from '@bodilenergy/util';
import { useAddressSavings } from '~src/hooks/graphql/useAddressSavings';
import { useBbrLookup } from '~src/hooks/graphql/useBbrLookup';
import { useFetchHeatPumpOutdoorUnits } from '~src/hooks/graphql/useHeatPump';
import { CUSTOMER_ADDRESS, useCachedState } from '~src/hooks/useCachedState';
import { useTakeover } from '~src/hooks/useTakeover';
import { AddressWithBbrId, ProductTypes } from '~src/model';
import { useDisplayContext } from '~src/context/DisplayContext';

const PAYBACK_MONTHS_MAX = 12 * 12;

export const useDisabledTabsAndProductTypes = (savings: ReturnType<typeof useAddressSavings>['data']) => {
  const [selectedProductTypes, setSelectedProductTypes] = useState<ProductTypes>(['heatPump']);

  const { data: outdoorUnits = [] } = useFetchHeatPumpOutdoorUnits() ?? {};

  const { leadOrigin } = useDisplayContext();

  const [address] = useCachedState<AddressWithBbrId>(CUSTOMER_ADDRESS);

  const { data: residence } = useBbrLookup(address);

  const residenceWithoutNull = deepNullToUndefined(residence);

  const solarTakeover = useTakeover(residenceWithoutNull, outdoorUnits, ['solar']);
  const heatPumpTakeover = useTakeover(residenceWithoutNull, outdoorUnits, ['heatPump']);

  const disabledProducts = useMemo(
    function updateDisabledTabs() {
      const paybackMonthsHeatPump = savings?.results[0].paybackMonths ?? 0;
      const paybackMonthsSolar = savings?.results[1].paybackMonths ?? 0;

      const isEDC = leadOrigin === 'edc';

      const maxHeatpumpPaybackMonthsExceed = isEDC ? paybackMonthsHeatPump > PAYBACK_MONTHS_MAX : false;
      const maxSolarPaybackMonthsExceed = isEDC ? paybackMonthsSolar > PAYBACK_MONTHS_MAX : false;

      return [
        ...(!!heatPumpTakeover || maxHeatpumpPaybackMonthsExceed || paybackMonthsHeatPump <= 0 ? ['heatPump'] : []),
        ...(!!solarTakeover || maxSolarPaybackMonthsExceed || paybackMonthsSolar <= 0 ? ['solar'] : []),
      ] as ProductTypes;
    },
    [savings?.results, heatPumpTakeover, solarTakeover]
  );

  useEffect(
    function updateSelectedProductTypes() {
      if (disabledProducts.length > 1) {
        return;
      }

      if (disabledProducts.length === 0) {
        setSelectedProductTypes(['heatPump', 'solar']);
        return;
      }

      setSelectedProductTypes(disabledProducts.includes('heatPump') ? ['solar'] : ['heatPump']);
    },
    [disabledProducts, setSelectedProductTypes]
  );

  return {
    disabledProducts,
    selectedProductTypes,
    setSelectedProductTypes,
    takeover: solarTakeover ?? heatPumpTakeover,
  };
};
